/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { adminApi } from '../../lib/api';

const AnnouncementTab = () => {
  const [cfg, setCfg] = useState({ text: '', is_active: false, speed_seconds: 30, bg_color: '#1a1410', text_color: '#f5f1e6' });
  const [initial, setInitial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const data = await adminApi.getAnnouncement();
        const normalized = {
          text: data.text || '',
          is_active: !!data.is_active,
          speed_seconds: data.speed_seconds || 30,
          bg_color: data.bg_color || '#1a1410',
          text_color: data.text_color || '#f5f1e6',
        };
        setCfg(normalized);
        setInitial(normalized);
      } catch (e) {
        setErr('Failed to load announcement settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const dirty = JSON.stringify(cfg) !== JSON.stringify(initial);

  const save = async () => {
    setSaving(true);
    setErr('');
    setMsg('');
    try {
      const res = await adminApi.setAnnouncement(cfg);
      const normalized = {
        text: res.text || '',
        is_active: !!res.is_active,
        speed_seconds: res.speed_seconds || 30,
        bg_color: res.bg_color || '#1a1410',
        text_color: res.text_color || '#f5f1e6',
      };
      setCfg(normalized);
      setInitial(normalized);
      setMsg('Announcement updated. Changes are live instantly.');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="mt-8 text-[#7a6a5a]">Loading…</div>;

  return (
    <div className="mt-8 max-w-2xl" data-testid="announcement-tab">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-[11px] tracking-[0.28em] text-[#3a2e26]">ANNOUNCEMENT MARQUEE</div>
          <p className="mt-2 text-sm text-[#5a4a3e] max-w-xl">
            A scrolling message bar shown above the site header on every page. Edit the text, turn it on or off, and save — changes appear instantly, no redeploy needed.
          </p>
        </div>
        <button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="announcement-save-btn"
          className="inline-flex items-center gap-2 bg-[#1a1410] text-[#f5f1e6] px-6 py-3 text-[11px] tracking-[0.32em] disabled:opacity-40"
        >
          <Save className="w-4 h-4" strokeWidth={1.6} />
          {saving ? 'SAVING…' : 'SAVE CHANGES'}
        </button>
      </div>

      {err && <div className="mb-4 p-3 bg-[#f3e3d8] border border-[#cdc2b1] text-sm text-[#7a3a2a]">{err}</div>}
      {msg && <div className="mb-4 p-3 bg-[#e6efe1] border border-[#b7ceac] text-sm text-[#3d5a2a]" data-testid="announcement-success">{msg}</div>}

      {/* Live preview */}
      {cfg.text.trim() && (
        <div className="mb-6 overflow-hidden" data-testid="announcement-preview" style={{ backgroundColor: cfg.bg_color, color: cfg.text_color }}>
          <div className="py-2 flex overflow-hidden">
            <div className="velouva-marquee" style={{ '--marquee-duration': `${cfg.speed_seconds}s` }}>
              {Array.from({ length: 8 }, (_, i) => (
                <span key={i} className="velouva-marquee__item text-[11px] sm:text-xs tracking-[0.22em] uppercase">{cfg.text}</span>
              ))}
              {Array.from({ length: 8 }, (_, i) => (
                <span key={`b${i}`} className="velouva-marquee__item text-[11px] sm:text-xs tracking-[0.22em] uppercase">{cfg.text}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="border border-[#e5dfd0] p-6 bg-white/50 space-y-5">
        <label className="flex items-center gap-3 text-sm">
          <input
            type="checkbox"
            checked={cfg.is_active}
            onChange={(e) => setCfg({ ...cfg, is_active: e.target.checked })}
            data-testid="announcement-active-toggle"
            className="w-4 h-4 accent-[#1a1410]"
          />
          Show announcement bar on the site
        </label>

        <div>
          <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">ANNOUNCEMENT TEXT</label>
          <textarea
            value={cfg.text}
            onChange={(e) => setCfg({ ...cfg, text: e.target.value.slice(0, 200) })}
            rows={2}
            maxLength={200}
            placeholder="Free shipping on orders over Rs. 999 · Use code WELCOME10 for 10% off"
            data-testid="announcement-text-input"
            className="mt-2 w-full bg-transparent border border-[#cdc2b1] focus:border-[#1a1410] p-3 text-sm focus:outline-none"
          />
          <p className="text-[10px] text-[#7a6a5a] mt-1">{cfg.text.length}/200 characters</p>
        </div>

        <div>
          <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a]">SCROLL SPEED: {cfg.speed_seconds}s per loop (lower = faster)</label>
          <input
            type="range"
            min={5}
            max={120}
            value={cfg.speed_seconds}
            onChange={(e) => setCfg({ ...cfg, speed_seconds: parseInt(e.target.value, 10) })}
            data-testid="announcement-speed-input"
            className="mt-2 w-full accent-[#1a1410]"
          />
        </div>

        <div className="flex gap-6">
          <div>
            <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a] block">BACKGROUND</label>
            <input
              type="color"
              value={cfg.bg_color}
              onChange={(e) => setCfg({ ...cfg, bg_color: e.target.value })}
              data-testid="announcement-bg-input"
              className="mt-2 w-16 h-10 border border-[#cdc2b1] bg-transparent cursor-pointer"
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.22em] text-[#7a6a5a] block">TEXT COLOR</label>
            <input
              type="color"
              value={cfg.text_color}
              onChange={(e) => setCfg({ ...cfg, text_color: e.target.value })}
              data-testid="announcement-textcolor-input"
              className="mt-2 w-16 h-10 border border-[#cdc2b1] bg-transparent cursor-pointer"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementTab;
